import { Fragment, useState, useEffect } from 'react';
import styles from './DraftPatientDetailInfoComponent.module.css';

// 표시 필드 정의 (라벨, DB 컬럼 키, 줄바꿈 여부)
const FIELDS = [
    { key: 'PatientCode',        label: 'Patient Code',        multiline: false },
    { key: 'orgin',              label: 'Origin',              multiline: false },
    { key: 'Gender',             label: 'Gender',              multiline: false },
    { key: 'Age',                label: 'Age',                 multiline: false },
    { key: 'Stage',              label: 'Stage',               multiline: false },
    { key: 'HistologicType',     label: 'Histologic Type',     multiline: false },
    { key: 'TreatmentHistory',   label: 'Treatment History',   multiline: true  },
    { key: 'clinicalAnnotation', label: 'Clinical Annotation', multiline: true  },
];

// ── SQL parseQuery (Banking 컴포넌트와 동일 — UPDATE/INSERT 처리) ────────────
const parseQuery = (query = '') => {
    const updates = {};
    if (!query) return updates;

    const normalized = query.replace(/\s+/g, ' ').trim();

    if (/insert\s+into/i.test(normalized) && /values/i.test(normalized)) {
        const insertRe = /insert\s+into\s+[`"]?(?:\w+\.)?[`"]?[\w`"]+\s*\(([^)]+)\)\s*values\s*\(([\s\S]+)\)/i;
        const match = normalized.match(insertRe);
        if (!match) return updates;

        const keys = match[1].split(',').map(k => k.trim().replace(/[`'"]/g, ''));
        const valuesRaw = match[2].trim();
        const values = [];
        let cur = '', inQuotes = false, parenCount = 0;
        for (let ch of valuesRaw) {
            if (ch === "'" && !inQuotes) { inQuotes = true; cur += ch; }
            else if (ch === "'" && inQuotes) { inQuotes = false; cur += ch; }
            else if (!inQuotes && ch === '(') { parenCount++; cur += ch; }
            else if (!inQuotes && ch === ')') { parenCount--; cur += ch; }
            else if (!inQuotes && ch === ',' && parenCount === 0) {
                values.push(cur.trim().replace(/^'+|'+$/g, ''));
                cur = '';
            } else { cur += ch; }
        }
        if (cur) values.push(cur.trim().replace(/^'+|'+$/g, ''));

        keys.forEach((key, i) => { updates[key] = values[i] ?? ''; });
    } else if (/update\s+/i.test(normalized) && /set\s+/i.test(normalized)) {
        const setRe = /set\s+(.+?)\s+where/i;
        const m = normalized.match(setRe);
        if (m) {
            m[1].split(/,(?=(?:[^']*'[^']*')*[^']*$)/).forEach(pair => {
                const [rawKey, rawVal] = pair.split('=');
                const key = rawKey.trim().replace(/['`"]/g, '');
                const val = rawVal.trim().replace(/['`"]/g, '');
                updates[key] = val;
            });
        }
    }

    return updates;
};

// 줄바꿈 텍스트를 <br>로 변환
const renderMultiline = (text) => {
    const str = text ?? '';
    if (!str) return '';
    return String(str).split('\n').map((line, idx) => (
        <Fragment key={idx}>
            {line}
            <br />
        </Fragment>
    ));
};

// 값 표시용 변환 (멀티라인 처리 포함)
const formatValue = (raw, multiline) => {
    if (raw == null || raw === '') return '';
    return multiline ? renderMultiline(raw) : String(raw);
};

function DraftPatientDetailInfoComponent({ selectedDraft, targetKey }) {
    const [patientData, setPatientData] = useState(null);
    const [draftUpdates, setDraftUpdates] = useState({});

    useEffect(() => {
        // 1) 원본 데이터 조회 (기존 환자 정보)
        if (targetKey) {
            fetch('/api/getPatientInfo_noLog', {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ param: targetKey }),
            })
                .then(res => res.json())
                .then(data => setPatientData(data?.length > 0 ? data[0] : null))
                .catch(() => setPatientData(null));
        }

        // 2) tempQuery 파싱 → 수정 내용 추출
        const tempQuery = selectedDraft?.tempQuery || '';
        setDraftUpdates(parseQuery(tempQuery));
    }, [targetKey, selectedDraft]);

    // 수정 기안일 때는 원본이 있어야 하고, 추가 기안일 때는 원본이 없을 수 있음
    const hasOriginal = patientData !== null;
    const hasUpdates = Object.keys(draftUpdates).length > 0;
    const isDraftApproved = selectedDraft?.status === 'approved';

    // 원본도 없고 수정 내용도 없으면 표시할 게 없음
    if (!hasOriginal && !hasUpdates) return null;

    const renderField = ({ key, label, multiline }) => {
        const originalRaw = patientData?.[key];
        const updatedRaw  = draftUpdates[key];

        const original = formatValue(originalRaw, multiline);
        const updated  = formatValue(updatedRaw, multiline);

        // 원본 raw와 updated raw를 문자열로 비교 (변경 감지용)
        const origStr = originalRaw != null ? String(originalRaw) : '';
        const updStr  = updatedRaw  != null ? String(updatedRaw)  : '';
        const isUpdated = updatedRaw != null && origStr !== updStr;

        // 원본이 없거나 승인된 기안: 수정값만 표시
        if (!hasOriginal || isDraftApproved) {
            return (
                <div className={styles.fieldRow} key={key}>
                    <div className={styles.fieldLabel}>{label}</div>
                    <div className={styles.fieldValue}>{updated || 'N/A'}</div>
                </div>
            );
        }

        return (
            <div className={styles.fieldRow} key={key}>
                <div className={styles.fieldLabel}>{label}</div>
                <div className={styles.fieldValue}>
                    {isUpdated ? (
                        <>
                            <span className={styles.originalValue}>{original || 'N/A'}</span>
                            {' '}→{' '}
                            <span className={styles.updatedValue}>{updated || 'N/A'}</span>
                        </>
                    ) : (original || 'N/A')}
                </div>
            </div>
        );
    };

    return (
        <div className={styles.main}>
            <div className={styles.draftTitle}>Patient Information</div>
            <div className={styles.infoContainer}>
                {FIELDS.map(renderField)}
            </div>
        </div>
    );
}

export default DraftPatientDetailInfoComponent;